import React, { useEffect, useState } from "react";
import { Card, Progress, Row, Col, Alert, Typography, Tooltip } from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarryOutOutlined,
  ScheduleOutlined,
  CoffeeOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useGetResourceStatusQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans, { calculateDelayDuration } from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";
import PieChart from "../../DashboardDraggable/Charts/PieChart";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";
import dayjs from "dayjs";

const { Text } = Typography;

// Enhanced color scheme matching the Service Request Dashboard
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
  danger: "#dc2626",

  // Status specific colors
  onShift: "#0f6936",
  busy: "#dc2626",
  pending: "#ea580c",
  idle: "#eab308",
  onBreak: "#8b5cf6",
  offShift: "#64748b",
};

export default function FSMResourceStatus({ refreshKey }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const {
    data: resourceStatus,
    isLoading,
    error,
    refetch,
  } = useGetResourceStatusQuery(
    { refreshKey },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    refetch();
  }, []);

  if (isLoading) return <Loader />;

  if (error)
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Alert
          message={trans("Failed to load resource status")}
          type="error"
          showIcon
        />
      </Card>
    );

  const {
    total = 0,
    breakdown = {},
    metrics = {},
    trends = [],
  } = resourceStatus || {};
  const {
    onShift = 0,
    busy = 0,
    pending = 0,
    idle = 0,
    offShift = 0,
    onBreak = 0,
  } = breakdown;
  const { utilizationRate = 0, shiftCoverage = 0, taskBacklog = 0 } = metrics;

  const activeStaff = onShift;
  const utilizationPercent =
    activeStaff > 0 ? Math.round((busy / activeStaff) * 100) : 0;

  // KPI Cards for header - matching Service Request Dashboard
  const kpis = [
    {
      title: trans("Total Staff"),
      value: total,
      icon: <TeamOutlined />,
      color: colorScheme.primary,
      tooltip: trans("Staff Count"),
      api: "useGetAllTechnicianQuery",
      extractData: (response) => response?.technicians || [],
      frontendFilter: null,
      columns: [
        { title: trans("Name"), dataIndex: "name", width: 200 },
        {
          title: trans("phone number"),
          dataIndex: "phone",
          width: 150,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("Email"),
          dataIndex: "email",
          width: 120,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("department"),
          dataIndex: "department",
          width: 180,
          render: (value) => value || "- - - - -",
        },
        {
          title: trans("Supervisor"),
          dataIndex: "supervisor_name",
          width: 180,
          render: (value) => value || "- - - - -",
        },
      ],
      query: {
        page: 1,
        pageSize: 10,
      },
      extractTotal: (response) => response?.total || 0,
    },
    {
      title: trans("Utilization"),
      value: utilizationRate,
      suffix: "%",
      icon: <ThunderboltOutlined />,
      color: colorScheme.info,
      tooltip: "Measure of how efficiently resources are used",
    },
    {
      title: trans("Coverage"),
      value: shiftCoverage,
      suffix: "%",
      icon: <ScheduleOutlined />,
      color: colorScheme.success,
      tooltip: "Extent of staff availability across operations",
    },
    {
      title: trans("Backlog"),
      value: taskBacklog,
      icon: <CarryOutOutlined />,
      color: colorScheme.warning,
      tooltip: trans("Pending workload / Outstanding tasks"),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        {
          title: trans("Delay Duration"),
          dataIndex: "startTime",
          width: 160,
          render: (value) => calculateDelayDuration(value),
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        status: "todo,unassigned",
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
  ];

  // Enhanced status breakdown - matching Service Request Dashboard interactive cards
  const statusItems = [
    // {
    //   key: "onShift",
    //   label: trans("On Shift"),
    //   desc: trans("Active technicians on duty"),
    //   value: onShift,
    //   icon: <ScheduleOutlined />,
    //   color: colorScheme.onShift,
    //   bgGradient: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
    // },
    {
      key: "busy",
      label: trans("Busy"),
      desc: trans("Working on active tasks"),
      value: busy,
      icon: <CheckCircleOutlined />,
      color: colorScheme.busy,
      bgGradient: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
    },
    {
      key: "pending",
      label: trans("Pending"),
      desc: trans("Tasks assigned, not started"),
      value: pending,
      icon: <CarryOutOutlined />,
      color: colorScheme.pending,
      bgGradient: "linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)",
    },
    {
      key: "available",
      label: trans("Available"),
      desc: trans("Ready for new assignments"),
      value: idle,
      icon: <UserOutlined />,
      color: colorScheme.onShift,
      bgGradient: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
    },
    {
      key: "onBreak",
      label: trans("On Break"),
      desc: trans("Currently on break"),
      value: onBreak,
      icon: <CoffeeOutlined />,
      color: colorScheme.onBreak,
      bgGradient: "linear-gradient(135deg, #f3e8ff 0%, #ddd6fe 100%)",
    },
    {
      key: "offShift",
      label: trans("Off Shift"),
      desc: trans("Not scheduled today"),
      value: offShift,
      icon: <ClockCircleOutlined />,
      color: colorScheme.offShift,
      bgGradient: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
    },
  ];

  const series = statusItems.map((item) => item.value); // numbers for pie
  const labels = statusItems.map((item) => item.label); // labels for pie
  const colors = statusItems.map((item) => item.color); // custom slice colors

  return (
    <div
      style={{
        // background: colorScheme.light,
        minHeight: "100%",
        padding: "16px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,

        display: "flex", // 👈 enable flex
        flexDirection: "column", // 👈 stack children vertically
      }}
    >
      {/* Header - Matching Service Request Dashboard style */}
      <SectionTitle
        title={"Resource Status"}
        subtitle={"Real-time Employee overview"}
        imgSrc={`${process.env.PUBLIC_URL}/assets/user-group.svg`}
        imgBgColor="#E6F4EA"
      />

      {/* KPI Cards - Matching Service Request Dashboard style */}
      <Row gutter={[12, 12]} style={{ marginBottom: "20px" }}>
        {kpis.map((kpi, i) => (
          <Col span={6} key={i}>
            <NumbersCard
              title={kpi.title}
              value={kpi.value}
              suffix={kpi.suffix}
              color={kpi.color}
              tooltip={kpi.tooltip}
              onClick={() => kpi.api && setSelectedAlert(kpi)}
              popup={kpi.api ? true : false}
            />
          </Col>
        ))}
      </Row>

      {/* Enhanced Utilization Progress */}
      <Tooltip
        title={trans(
          "Shows the percentage of active staff currently engaged in tasks"
        )}
      >
        <Card
          style={{
            borderRadius: "12px",
            border: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            marginBottom: "16px",
          }}
          bodyStyle={{ padding: "16px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <Text
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: colorScheme.dark,
              }}
            >
              {trans("Resource Utilization")}
            </Text>
            <Text style={{ fontSize: "12px", color: colorScheme.neutral }}>
              {busy} {trans("of")} {activeStaff} {trans("busy")}
            </Text>
          </div>
          <Progress
            percent={utilizationPercent}
            strokeColor={{
              "0%": colorScheme.primary,
              "100%": colorScheme.success,
            }}
            trailColor={colorScheme.border}
            showInfo
            style={{ marginBottom: "8px" }}
          />
        </Card>
      </Tooltip>
      {/* Enhanced Status Breakdown - Matching Service Request Dashboard */}
      <Card
        style={{
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          flexGrow: 1, // 👈 makes it take remaining space
          display: "flex", // ensure content fills vertically
          flexDirection: "column",
          height: "100%",
        }}
        bodyStyle={{ flex: 1, padding: "16px" }} // 👈 makes PieChart fill
        title={
          <Tooltip
            title={trans(
              "Visual breakdown of staff activity status across all shifts"
            )}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: "14px", fontWeight: "600" }}>
                  {trans("Status Distribution")}
                </span>
              </div>
              <Text style={{ fontSize: "12px", color: colorScheme.neutral }}>
                {total} {trans("total staff")}
              </Text>
            </div>
          </Tooltip>
        }
      >
        <PieChart
          data={series}
          labels={labels}
          color={colors}
          onSliceClick={(slice) => {
            const selectedStatus = statusItems[slice.index];
            setSelectedAlert({
              title: `${selectedStatus.label} (${slice.value})`,
              key: selectedStatus.key,
              color: selectedStatus.color,
              desc: selectedStatus.desc,
              api: "useGetTaskDistributionByStatusQuery",
              extractData: (response) => response?.data?.technicians || [],
              frontendFilter: null,
              columns: [
                { title: "Name", dataIndex: "technicianName", width: 200 },
                { title: "Phone", dataIndex: "phone", width: 150 },
                { title: "Email", dataIndex: "email", width: 120 },
                {
                  title: "Department",
                  dataIndex: "department",
                  width: 180,
                  render: (value) => value || "- - - - -",
                },
                {
                  title: "Supervisor",
                  dataIndex: "supervisor",
                  width: 180,
                },
              ],
              query: {
                page: 1,
                limit: 10,
                status: selectedStatus.key,
              },
              extractTotal: (response) => response?.data?.totalResults || 0,
            });
          }}
        />
      </Card>
      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
}
